import { IonSkeletonText } from '@ionic/react'
import React, { RefObject, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import ModeSwitch from '../../../components/ModeSwitch'
import { AppContext } from '../../../contexts/AppContext'
import useUser from '../../../contexts/hooks/useUser'
import { trpc } from '../../../modules/trpc'
import { SavingModeOptions } from '../utils/styles/savingModeOptions'
import LevelBadge from './LevelBadge'
import PointsCounter from './PointsCounter'

interface SavingHeaderProps {
	displayedPoints: number | null
	shouldAnimate: boolean
	pointsContainerRef: RefObject<HTMLDivElement>
	selectedMode: SavingModeOptions
	onModeChange: (mode: SavingModeOptions) => void
	modeOptions: { mode: SavingModeOptions; label: string }[]
}

const SavingHeader: React.FC<SavingHeaderProps> = ({
	displayedPoints,
	shouldAnimate,
	pointsContainerRef,
	selectedMode,
	onModeChange,
	modeOptions,
}) => {
	const { user } = useUser()

	const { isDarkMode } = useContext(AppContext)

	const { t } = useTranslation('pages_saving')

	const { data: currentSavings, isLoading: isCurrentSavingsLoading } =
		trpc.achievements.getUserCurrentSavings.useQuery(
			{ userId: user?.id ?? 0 },
			{ enabled: !!user?.id, staleTime: 6 * 60 * 60 * 1000 }
		)

	const { data: level, isLoading: isLevelLoading } = trpc.achievements.getUserCurrentLevel.useQuery(
		{ userId: user?.id ?? 0 },
		{ enabled: !!user?.id, staleTime: 6 * 60 * 60 * 1000 }
	)

	return (
		<div className='p-4 pt-0 layout-sm -mb-4 flex flex-col gap-6 z-20'>
			<div className='flex flex-col gap-2 mt-6 items-center'>
				<span className='text-sm text-white'>{t('monthPoints')}</span>

				<PointsCounter
					ref={pointsContainerRef}
					points={displayedPoints}
					shouldAnimate={shouldAnimate}
				/>

				<LevelBadge level={level?.result ?? 'noLevel'} isLoading={isLevelLoading} />

				{isCurrentSavingsLoading ? (
					<IonSkeletonText className='h-5 w-24' />
				) : (
					<p className='text-sm text-white'>
						{t('savings', { amount: (currentSavings?.result ?? 0).toFixed(2).replace('.', ',') })}
					</p>
				)}
			</div>

			<ModeSwitch<SavingModeOptions>
				options={modeOptions}
				currentMode={selectedMode}
				onModeChange={onModeChange}
				shadow
				customBackground={isDarkMode ? 'saving-switch-dark' : 'saving-switch'}
			/>
		</div>
	)
}

export default SavingHeader
